/* The search box: what the reader typed, and whether a message's headers
 * answer it. Pure, so the node tests drive it directly.
 *
 * **Plain words must all appear**, in the headers or (via the worker) the
 * text. **Field terms narrow by one field**: from:, to:, subject:, label:,
 * has:attachment, before: and after: a date. Quotes keep a phrase together.
 */

import { fold } from "../engine/text.js";

export function parseQuery(text) {
  const query = { text, terms: [], fields: [], before: null, after: null, attachment: false };

  for (const [, field, quoted, bare] of (text || "").matchAll(/(?:([a-z]+):)?(?:"([^"]*)"|(\S+))/gi)) {
    const raw = quoted ?? bare ?? "";
    const value = fold(raw);
    if (!value) continue;

    switch (field?.toLowerCase()) {
      case "from":
      case "to":
      case "subject":
      case "label":
        query.fields.push([field.toLowerCase(), value]);
        break;
      case "has":
        if (value.startsWith("attach")) query.attachment = true;
        break;
      case "before":
      case "after": {
        const date = Date.parse(raw);
        if (Number.isNaN(date)) query.terms.push(fold(`${field}:${raw}`));
        else query[field.toLowerCase()] = date;
        break;
      }
      default:
        query.terms.push(field ? fold(`${field}:${raw}`) : value);
    }
  }

  return query;
}

/** Is anything being asked, as opposed to an empty box? */
export function isFiltering(query) {
  return query.terms.length > 0 || query.fields.length > 0 || query.attachment
    || query.before !== null || query.after !== null;
}

/** The header text a plain term is looked for in, folded once per message and kept. */
export function headerText(summary) {
  return (summary.headerText ??= fold(
    `${summary.fromName} ${summary.fromAddress} ${summary.to} ${summary.cc} ${summary.subject} ${summary.labels}`));
}

function field(summary, name) {
  switch (name) {
    case "from": return `${summary.fromName} ${summary.fromAddress}`;
    case "to": return `${summary.to} ${summary.cc}`;
    case "subject": return summary.subject;
    default: return summary.labels;
  }
}

/**
 * Does a message pass everything the headers alone can decide? `textHit` says
 * whether the worker found every plain term in its text, which also covers
 * the headers: so a message passes if its headers have every term OR its text
 * does.
 */
export function matches(summary, query, textHit = false) {
  for (const [name, value] of query.fields) {
    if (!fold(field(summary, name)).includes(value)) return false;
  }

  if (query.attachment && !summary.maybeAttachments) return false;
  if (query.before !== null && !(summary.date !== null && summary.date < query.before)) return false;
  if (query.after !== null && !(summary.date !== null && summary.date >= query.after)) return false;
  if (!query.terms.length) return true;

  const header = headerText(summary);
  return textHit || query.terms.every((term) => header.includes(term));
}
